<script setup lang="ts">
import DashboardDoughnutChart from '@/components/rc/work-order-dashboard/DashboardDoughnutChart.vue'
import DashboardServicesBarChart from '@/components/rc/work-order-dashboard/DashboardServicesBarChart.vue'
import DashboardPartsBarChart from '@/components/rc/work-order-dashboard/DashboardPartsBarChart.vue'

import RlAnimatedNumber from '@/components/common/RlAnimatedNumber.vue'
import { useWorkOrder } from '@/composables/rc/work-order'
import { Icon } from '@iconify/vue'
import type { TabsPaneContext } from 'element-plus'
import { ref } from 'vue'

const props = defineProps<{ number: string }>()

const emit = defineEmits(['setMinerStatus', 'setPartStatus', 'setMissingService'])

const { workOrderDashboardSummaryQuery } = useWorkOrder(props.number)
const { isFetching, data, isError, error, isRefetching, refetch } = workOrderDashboardSummaryQuery()

enum Tab {
  PartsProgress,
  ServicesProgress,
}
const activeTab = ref<Tab>(Tab.PartsProgress)

const onTabClick = (tab: TabsPaneContext) => {
  activeTab.value = tab.props.name as Tab
}
</script>

<template>
  <rl-error-result v-if="isError && !isRefetching" :error="error">
    <el-button @click="refetch()">
      <Icon icon="material-symbols:refresh-rounded" :height="14" :inline="true" />&nbsp;Reload
    </el-button>
  </rl-error-result>
  <template v-if="data">
    <!-- Outermost container with two panels -->
    <div v-loading="isFetching || isRefetching" class="flex flex-col 2xl:flex-row gap-6">
      <!-- Right panel -->
      <div class="bg-fill-secondary rounded-lg p-spacing-lg flex-1 square flex flex-col items-center justify-center">
        <!-- Top stats -->
        <div class="flex justify-between w-full">
          <!-- Left units #/total -->
          <div class="flex flex-col gap-2">
            <p class="text-2xl font-bold">Miners</p>
            <p class="text-2xl font-bold">
              <span><RlAnimatedNumber :value="data.miners_status_statistics.total_completed" /></span
              ><span class="text-xl text-text-tertiary">/{{ data.work_order_information.expected_miners }}</span>
            </p>
          </div>
          <!-- Right percentage -->
          <p class="text-2xl font-bold">
            <RlAnimatedNumber :value="data.miners_status_statistics.completed_percentage" />%
          </p>
        </div>
        <!-- Chart -->
        <DashboardDoughnutChart
          v-if="data"
          :total-miners="data.work_order_information.expected_miners"
          :stats="data.miners_status_statistics"
          @arc-click="({ dataset, category }) => emit('setMinerStatus', category, dataset)"
        />
      </div>
      <!-- Right panel -->
      <div class="bg-fill-secondary rounded-lg p-8 flex-1 square flex flex-col items-center justify-center">
        <el-tabs v-model="activeTab" @tab-click="onTabClick">
          <el-tab-pane label="Parts progress" :name="Tab.PartsProgress" />
          <el-tab-pane label="Services progress" :name="Tab.ServicesProgress" />
        </el-tabs>
        <div v-show="activeTab == Tab.PartsProgress" class="w-full h-full">
          <!-- Top stats -->
          <div class="flex justify-between w-full">
            <!-- Left units #/total -->
            <p class="text-2xl font-bold">
              <span><RlAnimatedNumber :value="data.miner_parts_status_statistics.total_completed" /></span
              ><span class="text-xl text-text-tertiary">/{{ data.work_order_information.parts }}</span>
            </p>
            <!-- Right percentage -->
            <p class="text-2xl font-bold">
              <RlAnimatedNumber :value="data.miner_parts_status_statistics.completed_percentage" />%
            </p>
          </div>
          <DashboardPartsBarChart
            v-if="data"
            :stats="data.miner_parts_status_statistics"
            @bar-click="({ dataset, category }) => emit('setPartStatus', category, dataset)"
          />
        </div>
        <div v-show="activeTab == Tab.ServicesProgress" class="w-full h-full">
          <DashboardServicesBarChart
            v-if="data"
            :total-miners="data.work_order_information.expected_miners"
            :stats="data.services_statistics"
            @bar-click="({ dataset, category }) => emit('setMissingService', category, dataset)"
          />
        </div>
      </div>
    </div>
  </template>
</template>

<style scoped lang="scss">
@use '@/styles/element/overrides';

:deep() {
  @include overrides.custom-el-tabs;
  @include overrides.wide-el-tabs;
}
</style>
